/**
 * Single shared date/time formatting helper — QA-23/24 (86ca-timestamp-utc).
 *
 * Built on plain `Intl.DateTimeFormat`; no new dependency. react-intl /
 * FormatJS, Temporal, and date-fns v4 / Luxon / Day.js were all considered
 * and rejected for this ticket — see the ClickUp ticket for the full
 * rationale (react-intl is a translation framework pulled in for one prop;
 * Temporal isn't shippable without a polyfill yet; date-fns/Luxon solve
 * cross-zone arithmetic, which this ticket doesn't need — it only formats).
 *
 * Every visible timestamp in the app MUST go through this module — a bundle
 * scan found 1 caller pinning `timeZone: "UTC"` and dozens rendering in
 * whatever zone the visitor's OS happens to report, so two timestamps on
 * the same screen (e.g. a "data through" caption vs. a report's "last
 * seen") could differ by a full day and look inconsistent. Enforced by the
 * `no-restricted-syntax` ESLint rule in `eslint.config.js` (direct
 * `toLocaleDateString`/`toLocaleTimeString`/`new Intl.DateTimeFormat` calls
 * outside this file are a lint error).
 *
 * Zone: defaults to UTC. QA-25 lets a signed-in user pick their own zone
 * (persisted on `users.timezone`) — `setTimeZone()` is called once after
 * that profile loads or saves, so every already-rendered call site in the
 * app follows it without threading a zone parameter through 92 call sites.
 * An unauthenticated visitor (or one who never set a zone) always sees UTC.
 */

const STORAGE_KEY = 'dmarc_time_zone'
const DEFAULT_ZONE = 'UTC'

/** True if `tz` is a zone name `Intl.DateTimeFormat` accepts. */
export function isValidTimeZone(tz: string): boolean {
  try {
    // Probing for a RangeError is the only way Intl exposes zone validity —
    // the instance itself is unused.
    Intl.DateTimeFormat(undefined, { timeZone: tz })
    return true
  } catch {
    return false
  }
}

function readStoredZone(): string {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored && isValidTimeZone(stored)) return stored
  } catch {
    /* localStorage unavailable (SSR/private mode) — fall back to default */
  }
  return DEFAULT_ZONE
}

let currentZone: string = readStoredZone()

/**
 * Sets the zone every `formatDate`/`formatDateTime` call renders in from
 * this point on. Silently ignores an invalid/empty zone (keeps whatever
 * zone was already active) rather than throwing — callers pass
 * user-controlled profile data here (QA-25).
 */
export function setTimeZone(tz: string | null | undefined): void {
  if (!tz || !isValidTimeZone(tz)) return
  currentZone = tz
  try {
    localStorage.setItem(STORAGE_KEY, tz)
  } catch {
    /* ignore — persistence is a convenience, not a requirement */
  }
}

export function getTimeZone(): string {
  return currentZone
}

/** Resets to the UTC default. Test-only escape hatch. */
export function resetTimeZone(): void {
  currentZone = DEFAULT_ZONE
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch {
    /* ignore */
  }
}

function toDate(input: string | number | Date | null | undefined): Date | null {
  if (input === null || input === undefined || input === '') return null
  const d = input instanceof Date ? input : new Date(input)
  return Number.isNaN(d.getTime()) ? null : d
}

/** Short zone marker for the active zone, e.g. "UTC", "GMT-5", "EST". */
function zoneSuffix(): string {
  try {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: currentZone,
      timeZoneName: 'short',
    }).formatToParts(new Date())
    return parts.find((p) => p.type === 'timeZoneName')?.value ?? currentZone
  } catch {
    return currentZone
  }
}

/** Date only, with an explicit zone marker — e.g. "18 Mar 2026 (UTC)". */
export function formatDate(
  input: string | number | Date | null | undefined,
  fallback = '—'
): string {
  const d = toDate(input)
  if (!d) return fallback
  const datePart = new Intl.DateTimeFormat('en-GB', {
    timeZone: currentZone,
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(d)
  return `${datePart} (${zoneSuffix()})`
}

/**
 * Compact date, no year and no zone marker — for dense contexts (chart axis
 * ticks, table columns already labelled with a zone elsewhere on the same
 * screen) where a full marker on every value would be noise, e.g. "18 Mar".
 */
export function formatDateShort(
  input: string | number | Date | null | undefined,
  fallback = '—'
): string {
  const d = toDate(input)
  if (!d) return fallback
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: currentZone,
    month: 'short',
    day: 'numeric',
  }).format(d)
}

/**
 * Midnight (00:00) of `d`'s calendar date in the active zone, expressed as
 * a UTC timestamp — the building block for calendar-day arithmetic that
 * doesn't drift with time-of-day or DST.
 */
function zonedMidnightUTC(d: Date): number {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: currentZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(d)
  const get = (type: string) =>
    Number(parts.find((p) => p.type === type)?.value ?? 0)
  return Date.UTC(get('year'), get('month') - 1, get('day'))
}

/**
 * Whole calendar days from `now` to `target`, in the active zone —
 * QA-14: a naive `Math.floor(msDiff / 86400000)` on two instants that are
 * exactly N calendar days apart (e.g. a 90-day trial's start/end) truncates
 * to N-1 the moment any time has elapsed since `now` was captured, because
 * the millisecond difference is then N days minus a few hours, not a clean
 * N. Comparing calendar dates (not raw milliseconds) avoids that off-by-one
 * and any DST-related drift. Returns 0 for invalid/missing `target`.
 */
export function daysUntil(
  target: string | number | Date | null | undefined,
  now: Date = new Date()
): number {
  const t = toDate(target)
  if (!t) return 0
  return Math.round((zonedMidnightUTC(t) - zonedMidnightUTC(now)) / 86400000)
}

/** Date + time + explicit zone marker — e.g. "18 Mar 2026, 14:05 (UTC)". */
export function formatDateTime(
  input: string | number | Date | null | undefined,
  fallback = '—'
): string {
  const d = toDate(input)
  if (!d) return fallback
  const formatted = new Intl.DateTimeFormat('en-GB', {
    timeZone: currentZone,
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(d)
  return `${formatted} (${zoneSuffix()})`
}
